import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subscription, of, switchMap, tap, throwError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { whiteLabelTarget } from 'src/app/utils/whiteLabel';
import { SupportActivityTrackerService } from '../services/support-activity-tracker.service';
import { SupportChatService } from '../services/support-chat.service';
import { SupportChatLauncherService } from '../services/support-chat-launcher.service';
import { SupportAttachmentPreviewService } from '../services/support-attachment-preview';
import { SUPPORT_ATTACHMENT_LIMITS, SUPPORT_FEATURES } from '../support-system.config';
import {
  SupportAttachmentNotice,
  SupportDraftAttachment,
  SupportUploadQueue,
  fileIcon,
  fileSize,
  hasFiles,
  isImageType,
  namePastedImage,
  rejectionNotice,
  uploadFailureKey,
} from '../services/support-upload-queue';
import {
  SupportAttachment,
  SupportConversation,
  SupportErrorCode,
  SupportMessage,
} from '../support-system.models';
import {
  SupportReportDialogComponent,
  SupportReportDialogData,
} from '../report-dialog/support-report-dialog.component';

type ChatView = 'home' | 'messages' | 'thread';
type ChatTab = 'home' | 'messages';

const MAX_MESSAGE_LENGTH = 4000;

/** What a failed send tells the user, by backend error code. */
const FAILURE_KEYS: Partial<Record<SupportErrorCode, string>> = {
  RATE_LIMITED: 'support_chat.error_rate_limited',
  CONFLICT: 'support_chat.error_closed',
  UNAVAILABLE: 'support_chat.error_unavailable',
};

@Component({
  selector: 'app-support-chat-box',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextFieldModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.css'],
})
export class SupportChatBoxComponent implements OnInit, OnDestroy {
  readonly chat = inject(SupportChatService);
  private tracker = inject(SupportActivityTrackerService);
  private preview = inject(SupportAttachmentPreviewService);
  private launcher = inject(SupportChatLauncherService);
  private dialog = inject(MatDialog);
  private localStorageService = inject(LocalStorageService);
  private targetService = inject(CoreTargetService);

  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;
  @ViewChild('composer') composer?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('filePicker') filePicker?: ElementRef<HTMLInputElement>;

  readonly features = SUPPORT_FEATURES;
  readonly maxLength = MAX_MESSAGE_LENGTH;
  readonly maxFileMb = SUPPORT_ATTACHMENT_LIMITS.maxBytes / (1024 * 1024);
  readonly fileAccept = SUPPORT_ATTACHMENT_LIMITS.mimeTypes.join(',');
  readonly logo = whiteLabelTarget?.logo.white || 'assets/img/behamics_logo_white.svg';

  readonly isOpen = signal(false);
  /** The report modal is up: the floating buttons go under its backdrop. */
  readonly reportOpen = signal(false);
  readonly view = signal<ChatView>('home');
  readonly activeTab = signal<ChatTab>('home');
  readonly activeConversationId = signal<string | null>(null);

  readonly activeConversation = computed(() =>
    this.chat.findConversation(this.activeConversationId()),
  );
  readonly latestConversation = computed(
    () => this.chat.conversations()[0] || null,
  );
  /** A new conversation can't take a 2nd message until its 1st one is saved. */
  readonly composerLocked = computed(() =>
    this.chat.isPending(this.activeConversation()),
  );
  /** The thread's messages plus a "<name> joined" line at the take-over. */
  readonly threadMessages = computed<SupportMessage[]>(() => {
    const conversation = this.activeConversation();
    if (!conversation) return [];
    const { messages, assignedAt, assignedManagerName } = conversation;
    const joinedVisible =
      assignedAt &&
      assignedManagerName &&
      conversation.messagesLoaded &&
      // Only when the take-over falls inside the loaded messages.
      (!conversation.olderCursor || assignedAt >= (messages[0]?.createdAt || ''));
    if (!joinedVisible) return messages;

    const joined: SupportMessage = {
      id: `assigned-${conversation.id}`,
      clientId: `assigned-${conversation.id}`,
      conversationId: conversation.id,
      author: 'system',
      body: '',
      createdAt: assignedAt!,
      status: 'sent',
      // No employee name: clients only ever see "the Behamics team".
      event: { type: 'assigned' },
    };
    const index = messages.findIndex((m) => m.createdAt > assignedAt!);
    return index === -1
      ? [...messages, joined]
      : [...messages.slice(0, index), joined, ...messages.slice(index)];
  });
  /** Changes only when a message is added at the end (not "load earlier"). */
  private readonly lastMessageKey = computed(() => {
    const messages = this.activeConversation()?.messages || [];
    const last = messages[messages.length - 1];
    return last ? `${last.clientId}:${last.status}` : '';
  });
  readonly loadingOlder = signal(false);
  readonly connectionLost = computed(
    () => this.chat.connection() === 'disconnected',
  );

  /** Files for the chat composer: uploaded into the active chat. */
  readonly composerFiles = new SupportUploadQueue((file) =>
    this.ensureActiveConversation().pipe(
      switchMap((id) => this.chat.uploadAttachment(id, file)),
    ),
  );
  /** Signed thumbnail links by attachment id (valid 5 minutes). */
  readonly imageUrls = signal<Record<string, string>>({});
  private readonly requestedImages = new Set<string>();
  private readonly retriedImages = new Set<string>();
  /** Why the last dropped/picked/pasted files were not all added. */
  readonly attachmentNotice = signal<SupportAttachmentNotice | null>(null);
  readonly dragging = signal(false);
  /** Images whose URL no longer loads (local blob: URLs after a reload). */
  readonly brokenImages = new Set<string>();
  private dragDepth = 0;
  private launchRequests?: Subscription;

  firstName = '';
  draft = '';

  constructor() {
    // Fetch a signed link for every image shown in the thread.
    effect(
      () => {
        for (const message of this.threadMessages()) {
          for (const file of message.attachments || []) {
            if (file.localUrl || !isImageType(file.mimeType)) continue;
            if (this.requestedImages.has(file.id)) continue;
            this.loadImageUrl(message, file.id);
          }
        }
      },
      { allowSignalWrites: true },
    );

    // Keep the newest message in view.
    effect(() => {
      if (this.view() !== 'thread' || !this.lastMessageKey()) return;
      setTimeout(() => this.scrollToBottom());
    });
  }

  ngOnInit(): void {
    const name = this.localStorageService.get<string>('name') || '';
    this.firstName = name.trim().split(/\s+/)[0] || '';
    this.chat.init();
    this.launcher.attach();
    this.launchRequests = this.launcher.requests$.subscribe((message) =>
      this.openWith(message),
    );
  }

  ngOnDestroy(): void {
    this.launcher.detach();
    this.launchRequests?.unsubscribe();
    // The shell is gone (logout, full-screen page): stop listening.
    this.chat.suspend();
    this.composerFiles.clear();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // Escape in the file preview closes the preview, not the chat.
    if (this.preview.isOpen()) return;
    if (this.isOpen()) this.close();
  }

  /* ------------------------------ Panel ------------------------------- */

  toggle(): void {
    this.isOpen() ? this.close() : this.openPanel();
  }

  openPanel(): void {
    this.isOpen.set(true);
    if (this.view() === 'thread') {
      this.chat.setOpenConversation(this.activeConversationId());
    }
  }

  /** "Need help?" elsewhere in the app: this shop's chat, with the text typed in. */
  private openWith(message: string): void {
    this.activeTab.set('home');
    this.startMessage();
    if (message) this.draft = message;
    this.openPanel();
  }

  close(): void {
    this.isOpen.set(false);
    this.chat.setOpenConversation(null);
  }

  setTab(tab: ChatTab): void {
    this.activeTab.set(tab);
    this.view.set(tab);
    this.activeConversationId.set(null);
    this.chat.setOpenConversation(null);
    if (tab === 'messages') this.chat.refresh();
  }

  back(): void {
    this.setTab(this.activeTab());
  }

  /* ---------------------------- Conversations ---------------------------- */

  /** Continues the open chat for the current shop, or starts one. */
  startMessage(): void {
    const existing = this.chat.openConversationFor(this.currentTarget());
    if (existing) {
      this.openConversation(existing);
      return;
    }
    this.activeConversationId.set(null);
    this.chat.setOpenConversation(null);
    this.draft = '';
    this.resetFiles(this.composerFiles);
    this.view.set('thread');
    this.focus(() => this.composer);
  }

  openConversation(conversation: SupportConversation): void {
    this.activeConversationId.set(conversation.id);
    this.chat.loadMessages(conversation.id);
    this.chat.setOpenConversation(conversation.id);
    this.draft = '';
    this.resetFiles(this.composerFiles);
    this.view.set('thread');
    this.focus(() => this.composer);
  }

  /** Text or at least one file, and no upload still running or failed. */
  get canSend(): boolean {
    return (
      (!!this.draft.trim() ||
        (this.features.attachments && !!this.composerFiles.count())) &&
      this.composerFiles.settled() &&
      !this.composerLocked()
    );
  }

  send(): void {
    if (!this.canSend) return;
    const body = this.draft.trim();
    const attachments = this.composerFiles.take();
    this.attachmentNotice.set(null);

    const id = this.activeConversationId();
    if (id) {
      this.chat.sendMessage(id, body, attachments);
    } else {
      const newId = this.chat.startConversation(body, this.currentTarget(), attachments);
      this.activeConversationId.set(newId);
      this.chat.setOpenConversation(newId);
    }
    this.draft = '';
    this.focus(() => this.composer);
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  retry(message: SupportMessage): void {
    this.chat.retry(message);
  }

  /** Older messages go on top; keep the user's place in the thread. */
  loadOlder(): void {
    const id = this.activeConversationId();
    const list = this.messageList?.nativeElement;
    if (!id || this.loadingOlder()) return;
    const fromBottom = list ? list.scrollHeight - list.scrollTop : 0;
    this.loadingOlder.set(true);
    this.chat.loadOlder(id).subscribe({
      next: () => {
        this.loadingOlder.set(false);
        setTimeout(() => {
          if (list) list.scrollTop = list.scrollHeight - fromBottom;
        });
      },
      error: () => this.loadingOlder.set(false),
    });
  }

  failureKey(message: SupportMessage): string {
    return (message.error && FAILURE_KEYS[message.error]) || 'support_chat.not_sent';
  }

  hasSeveralTargets(): boolean {
    return (this.chat.me()?.targets?.length || 0) > 1;
  }

  /* ---------------------------- Issue report ---------------------------- */

  /**
   * The report is a form in a modal, answered by email (not a chat). The
   * activity history is frozen now: it should describe the problem, not the
   * typing. It is sent with the report but not shown to the user.
   */
  startReport(): void {
    if (!this.features.issueReports) return;
    const data: SupportReportDialogData = {
      target: this.currentTarget(),
      showTarget: this.hasSeveralTargets(),
      snapshot: this.tracker.snapshot(),
    };
    this.close();
    this.reportOpen.set(true);
    this.dialog
      .open(SupportReportDialogComponent, {
        data,
        width: '560px',
        maxWidth: '96vw',
        panelClass: 'support-report-dialog-panel',
      })
      .afterClosed()
      .subscribe(() => this.reportOpen.set(false));
  }

  /* ----------------------------- Attachments ----------------------------- */

  /** Paperclip, paste and drop work in an open chat, if uploads are on. */
  canAttach(): boolean {
    return (
      this.features.attachments &&
      this.chat.attachmentsAvailable() &&
      this.activeConversation()?.status !== 'closed'
    );
  }

  /** Files can only be added to the conversation on screen. */
  private get activeFiles(): SupportUploadQueue | null {
    return this.view() === 'thread' && this.canAttach() ? this.composerFiles : null;
  }

  pickFiles(): void {
    this.filePicker?.nativeElement.click();
  }

  onFilesPicked(input: HTMLInputElement): void {
    this.addFiles(Array.from(input.files || []));
    input.value = '';
  }

  /** Screenshots pasted into either text box become attachments. */
  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files || []);
    if (!files.length) return;
    event.preventDefault();
    this.addFiles(files.map(namePastedImage));
  }

  onDragEnter(event: DragEvent): void {
    if (!this.activeFiles || !hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth++;
    this.dragging.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.activeFiles || !hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(): void {
    if (!this.dragging()) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (!this.dragDepth) this.dragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.activeFiles || !hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.dragging.set(false);
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  removeFile(queue: SupportUploadQueue, localId: string): void {
    queue.remove(localId);
    this.attachmentNotice.set(null);
  }

  isImage(attachment: SupportAttachment): boolean {
    return (
      isImageType(attachment.mimeType) && !this.brokenImages.has(attachment.id)
    );
  }

  /** Our own preview, else the signed link once it's there. */
  imageSrc(attachment: SupportAttachment): string | null {
    return attachment.localUrl || this.imageUrls()[attachment.id] || null;
  }

  /** An expired link (e.g. the thread re-rendered later) gets one refresh. */
  onImageError(message: SupportMessage, attachment: SupportAttachment): void {
    if (attachment.localUrl || this.retriedImages.has(attachment.id)) {
      this.brokenImages.add(attachment.id);
      return;
    }
    this.retriedImages.add(attachment.id);
    this.loadImageUrl(message, attachment.id);
  }

  /**
   * Images, PDFs and videos open in the full-screen preview. Other files
   * open (or download) in a new tab, opened inside the click so popup
   * blockers allow it, with a fresh signed link.
   */
  openAttachment(message: SupportMessage, attachment: SupportAttachment): void {
    // Images, PDFs and videos open in the dashboard's full-screen preview.
    if (
      this.preview.canPreview(attachment.mimeType) &&
      (message.status === 'sent' || attachment.localUrl)
    ) {
      this.preview.open(message.attachments || [], attachment, (file) =>
        file.localUrl
          ? of(file.localUrl)
          : message.status === 'sent'
            ? this.chat.attachmentUrl(message.conversationId, file.id)
            : throwError(() => new Error('Not uploaded yet')),
      );
      return;
    }
    if (message.status !== 'sent') {
      if (attachment.localUrl) window.open(attachment.localUrl, '_blank', 'noopener');
      return;
    }
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    this.chat.attachmentUrl(message.conversationId, attachment.id).subscribe({
      next: (url) => {
        if (tab) tab.location.href = url;
        else window.open(url, '_blank', 'noopener');
      },
      error: () => tab?.close(),
    });
  }

  readonly fileIcon = fileIcon;
  readonly fileSize = fileSize;
  uploadFailureKey(item: SupportDraftAttachment): string {
    return uploadFailureKey(item);
  }

  /* ------------------------------ Display ------------------------------ */

  lastMessage(conversation: SupportConversation): SupportMessage | null {
    return conversation.messages[conversation.messages.length - 1] || null;
  }

  /** Time is shown once per run of messages from the same author. */
  isLastInGroup(messages: SupportMessage[], index: number): boolean {
    const next = messages[index + 1];
    return !next || next.author !== messages[index].author;
  }

  isToday(iso: string): boolean {
    return new Date(iso).toDateString() === new Date().toDateString();
  }

  trackById(_: number, item: { id: string | number }): string | number {
    return item.id;
  }

  trackByClientId(_: number, message: SupportMessage): string {
    return message.clientId;
  }

  /* ------------------------------ Internals ----------------------------- */

  /** The selected shop if it's one of the user's, else their default. */
  private currentTarget(): string | undefined {
    return this.chat.targetFor(this.targetService.getSelectedTarget('target') || '');
  }

  /**
   * The chat files go into. A new conversation is created on the server
   * by its first file, and the thread switches to it.
   */
  private ensureActiveConversation(): Observable<string> {
    const current = this.activeConversation();
    if (current && !this.chat.isPending(current)) return of(current.id);
    return this.chat.ensureConversation(this.currentTarget()).pipe(
      tap((id) => {
        if (this.view() === 'thread' && !this.activeConversation()) {
          this.activeConversationId.set(id);
          this.chat.setOpenConversation(id);
        }
      }),
    );
  }

  private loadImageUrl(message: SupportMessage, attachmentId: string): void {
    this.requestedImages.add(attachmentId);
    this.chat.attachmentUrl(message.conversationId, attachmentId).subscribe({
      next: (url) => this.imageUrls.update((urls) => ({ ...urls, [attachmentId]: url })),
      error: () => this.brokenImages.add(attachmentId),
    });
  }

  private addFiles(files: File[]): void {
    const queue = this.activeFiles;
    if (!queue || !files.length) return;
    this.attachmentNotice.set(rejectionNotice(queue.add(files)));
  }

  private resetFiles(queue: SupportUploadQueue): void {
    queue.clear();
    this.attachmentNotice.set(null);
  }

  private focus(target: () => ElementRef<HTMLElement> | undefined): void {
    setTimeout(() => target()?.nativeElement.focus());
  }

  private scrollToBottom(): void {
    const el = this.messageList?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
